import {
  AppBar,
  Avatar,
  Box,
  Button,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { FormControl, useTheme } from "@mui/material";
import { Info, Menu as MenuIcon } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { useState } from "react";
import AssetSelector from "../../Selects/AssetSelector/AssetSelector";
import CategorizedSelect from "../../Selects/CategorizedSelect";
import HelpIcon from "@mui/icons-material/Help";
import ModalDialog from "../../Modals/ModalDialog/ModalDialog";
import SimpleTextField from "../../Forms/FieldTypes/TextField";
import UserProfile from "../../../pages/users/UserProfile";

export default function TopNav(props) {
  const {
    apiUrl,
    disableTextBox = false,
    noOptionsText,
    onItemClick = () => { },
    onSearchBarChange = () => { },
    onSearchBarClose = () => { },
    searchResults = [],
    searchText = {},
    token,
    useAssetSearch = true,
    useTextBox = false,
    useCategorizedSelect = false,
  } = props;
  const theme = useTheme();
  const { palette = {} } = theme;
  const { common = {}, primary = {} } = palette;
  const classes = {
    avatar: {
      backgroundColor: common?.white,
      color: primary?.main,
    },
    dropdownAvatar: {
      backgroundColor: primary?.main,
      color: common?.white,
    },
  };

  const [anchorElNav, setAnchorElNav] = useState(null);
  const [anchorElUser, setAnchorElUser] = useState(null);
  const [anchorElInfo, setAnchorElInfo] = useState(null);
  const [moreEl, setMoreEl] = useState(null);
  const [userModalOpen, setUserModalOpen] = useState(false);

  const handleNavMenuOpen = (event) => {
    setAnchorElNav(event.currentTarget);
  };

  const handleUserMenuOpen = (event) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleInfoMenuOpen = (event) => {
    setAnchorElInfo(event.currentTarget);
  };

  const handleMoreMenuOpen = (event) => {
    setMoreEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorElNav(null);
    setAnchorElInfo(null);
    setAnchorElUser(null);
    setMoreEl(null);
  };

  const navLinks = [];

  const firstInitial = localStorage
    .getItem("firstName")
    ?.charAt(0)
    .toUpperCase();
  const lastInitial = localStorage.getItem("lastName")?.charAt(0).toUpperCase();
  const initials =
    firstInitial && lastInitial ? `${firstInitial}${lastInitial}` : null;

  const email = localStorage.getItem("userEmail");

  const versionNumber = "v3.0.1";

  return (
    <Box>
      <ModalDialog
        open={userModalOpen}
        handleClose={() => setUserModalOpen(false)}
        title="User Profile"
        content={<UserProfile apiUrl={apiUrl} token={token} />}
      />
      <AppBar
        sx={{
          backgroundColor: primary?.main,
          boxShadow: "none",
          paddingLeft: "95px", // using 95 because the width of sidenav is 90.
        }}
      >
        <Toolbar>
          {/* Left Section: Navigation Links */}
          <Box
            sx={{
              alignItems: "center",
              display: { xs: "none", md: "flex" },
              flexGrow: 1,
            }}
          >
            {navLinks.map((link) => (
              <Button key={link} color="inherit" onClick={handleNavMenuOpen}>
                {link}
              </Button>
            ))}
            {/* Dropdown for "More" */}
            {navLinks.length > 0 && (
              <>
                <Button color="inherit" onClick={handleMoreMenuOpen}>
                  More
                </Button>
                <Menu
                  anchorEl={moreEl}
                  open={Boolean(moreEl)}
                  onClose={handleClose}
                >
                  <MenuItem onClick={handleClose}>More Link 1</MenuItem>
                  <MenuItem onClick={handleClose}>More Link 2</MenuItem>
                </Menu>
              </>
            )}
          </Box>

          {/* Center Section: Search Field */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "flex", md: "flex" },
              justifyContent: "center",
              marginRight: { xs: 2, md: 0 }, // Adds margin-right on xs screens, no margin for md and above
              marginLeft: { xs: 2, md: 0 }, // Adds margin-left on xs screens, no margin for md and above
            }}
          >
            {useAssetSearch ? <AssetSelector {...props} /> : null}
            {useTextBox ?
              <FormControl sx={{ width: "100%" }} variant="filled">
                <SimpleTextField
                  disabled={disableTextBox}
                  fullWidth
                  externalLabel={false}
                  label="Search for an asset..."
                  onChange={onSearchBarChange}
                  sx={{
                    backgroundColor: "white",
                    color: 'black'
                  }}
                  variant="filled"
                  value={searchText}
                />
              </FormControl>
              : null}
            {useCategorizedSelect ?
              <FormControl sx={{ width: "100%" }} variant="filled">
                <CategorizedSelect
                  assets={searchResults}
                  noOptionsText={noOptionsText}
                  onClose={onSearchBarClose}
                  onItemClick={onItemClick}
                  value={searchText}
                  onChange={onSearchBarChange}
                />
              </FormControl>
              :
              null}
          </Box>

          {/* Right Section: Icons */}
          <Box
            sx={{
              alignItems: "center",
              flexGrow: 1,
              display: { xs: "none", md: "flex" },
              justifyContent: "flex-end",
            }}
          >
            <Typography
              component={"span"}
              sx={{ color: "white", paddingRight: "10px" }}
            >
              {versionNumber}
            </Typography>

            <IconButton color="inherit">
              <Tooltip title="Support" placement="bottom">
                <Link
                  href="http://helpdesk.locatorx.com/support/home"
                  rel="noopener noreferrer"
                  sx={{
                    color: "inherit",
                    "&:hover": {
                      color: "inherit",
                    },
                    textDecoration: "none",
                    marginBottom: "3px",
                  }}
                  target="_blank"
                >
                  <HelpIcon />
                </Link>
              </Tooltip>
            </IconButton>

            {/* Info Icon (has menu) */}
            <IconButton color="inherit" onClick={handleInfoMenuOpen}>
              <Tooltip title="Information" placement="bottom">
                <Info />
              </Tooltip>
            </IconButton>

            {/* User Avatar/Profile (has menu) */}
            <IconButton color="inherit" onClick={handleUserMenuOpen}>
              <Tooltip title={email} placement="right">
                <Avatar sx={classes.avatar}>{initials}</Avatar>
              </Tooltip>
            </IconButton>

            {/* Menu for Information */}
            <Menu
              anchorEl={anchorElInfo}
              open={Boolean(anchorElInfo)}
              onClose={handleClose}
            >
              {/* privacy policy link */}
              <Link
                href="https://wiki.locatorx.com/privacy-policy/"
                rel="noopener noreferrer"
                sx={{ textDecoration: "none", color: "inherit" }}
                target="_blank"
              >
                <MenuItem>Privacy Policy</MenuItem>
              </Link>
              {/* terms of use link */}
              <Link
                href="https://wiki.locatorx.com/terms-of-use/"
                rel="noopener noreferrer"
                sx={{ textDecoration: "none", color: "inherit" }}
                target="_blank"
              >
                <MenuItem>Terms of Use</MenuItem>
              </Link>
            </Menu>

            {/* Menu for User Profile */}
            <Menu
              anchorEl={anchorElUser}
              open={Boolean(anchorElUser)}
              onClose={handleClose}
            >
              <MenuItem onClick={() => setUserModalOpen(true)}>
                Profile
              </MenuItem>
              <RouterLink
                to={{ pathname: "/logout" }}
                style={{
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <MenuItem onClick={handleClose}>Logout</MenuItem>
              </RouterLink>
            </Menu>
          </Box>

          {/* Responsive Hamburger Menu */}
          <Box
            sx={{
              display: { xs: "flex", md: "none" },
            }}
          >
            <IconButton
              color="inherit"
              edge="start"
              onClick={handleNavMenuOpen}
            >
              <MenuIcon />
            </IconButton>
            <Menu
              anchorEl={anchorElNav}
              open={Boolean(anchorElNav)}
              onClose={handleClose}
              sx={{
                alighItems: "center",
                justifyContent: "center",
              }}
            >
              {navLinks.length > 0 && (
                <>
                  {navLinks.map((link) => (
                    <MenuItem key={link} onClick={handleClose}>
                      {link}
                    </MenuItem>
                  ))}
                  <MenuItem onClick={handleClose}>More</MenuItem>
                </>
              )}

              <MenuItem onClick={() => setUserModalOpen(true)}>
                Profile
              </MenuItem>

              <RouterLink
                to={{ pathname: "/logout" }}
                style={{ textDecoration: "none", color: "inherit" }}
              >
                <MenuItem onClick={handleClose}>Logout</MenuItem>
              </RouterLink>
              <Link
                href="http://helpdesk.locatorx.com/support/home"
                rel="noopener noreferrer"
                style={{ textDecoration: "none", color: "inherit" }}
                target="_blank"
              >
                <MenuItem>Support</MenuItem>
              </Link>
              <MenuItem divider={true} onClick={handleInfoMenuOpen}>
                Information
              </MenuItem>

              <MenuItem
                sx={{
                  cursor: "default",
                  justifyContent: "center",
                  textDecoration: "none",
                }}
              >
                {versionNumber}
              </MenuItem>
              <MenuItem
                sx={{
                  cursor: "default",
                  justifyContent: "center",
                  textDecoration: "none",
                }}
              >
                <Tooltip title={email} placement="left">
                  <Avatar sx={classes.dropdownAvatar}>{initials}</Avatar>
                </Tooltip>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
    </Box>
  );
}
